import { fromMaybe } from "../../lib/maybe";
/**
 * Entity underlines for the ProseMirror editor (#250): the matching in
 * `entityPeekIndex.ts`, run over the plain-text projection and
 * mapped back to document ranges. The hover / caret peek card is a
 * `hoverCards` source (see `peekHoverSource`).
 */

import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import { Decoration, DecorationSet, type EditorView } from "prosemirror-view";
import { matchMaybe } from "../../lib/maybe";
import {
  entityEffectiveColor,
  entityRefAccentAttributes,
} from "../../lib/entityColors";
import { textOffsetToDocPos } from "../../lib/zwdoc/plainText";
import {
  CARET_PEEK_MS,
  HOVER_TIME_MS,
  editorContentWidth,
  renderEntityPeekCard,
  type EntityPeekManuscriptContext,
} from "../entityPeekCard";
import {
  entityPeekMatchAt,
  findEntityPeekMatches,
  type EntityPeekEntry,
  type EntityPeekMatch,
} from "../entityPeekIndex";
import type { HoverSource } from "./hoverCards";
import { projectionOf } from "./projection";

/** A match with both its document range and its projection range. */
export type ProseEntityMatch = EntityPeekMatch & {
  textFrom: number;
  textTo: number;
};

/** Shell context the peek card needs (avatar root, arc, navigation). */
export type ProsePeekContext = {
  rootPath: () => string | null;
  manuscript: () => EntityPeekManuscriptContext | null;
  openEntry: (fileName: string) => void;
};

type PeekState = {
  matches: readonly ProseEntityMatch[];
  decorations: DecorationSet;
};

const peekKey = new PluginKey<PeekState>("zwEntityPeek");

function computePeek(
  state: EditorState,
  index: readonly EntityPeekEntry[],
): PeekState {
  if (index.length === 0) {
    return { matches: [], decorations: DecorationSet.empty };
  }
  const projection = projectionOf(state);
  const matches = findEntityPeekMatches(projection.text, index, false).map(
    (match): ProseEntityMatch => ({
      entry: match.entry,
      textFrom: match.from,
      textTo: match.to,
      from: textOffsetToDocPos(projection, match.from),
      to: textOffsetToDocPos(projection, match.to),
    }),
  );
  const decorations = matches
    .filter((match) => match.from < match.to)
    .map((match) => {
      const accent = entityRefAccentAttributes(
        entityEffectiveColor(match.entry.color, match.entry.type),
      );
      return Decoration.inline(match.from, match.to, {
        class: "zw-entity-ref",
        "data-entity-id": match.entry.fileName,
        ...matchMaybe(accent, {
          nothing: () => ({}),
          just: (attributes) => attributes,
        }),
      });
    });
  return { matches, decorations: DecorationSet.create(state.doc, decorations) };
}

/**
 * Underlines Database entity names from `index()`. Recomputes on every doc
 * change and on `refreshEntityPeek`. Must come after `projectionPlugin`.
 */
export function entityPeekPlugin(
  index: () => readonly EntityPeekEntry[],
): Plugin<PeekState> {
  return new Plugin<PeekState>({
    key: peekKey,
    state: {
      init: (_config, state) => computePeek(state, index()),
      apply: (tr, value, _old, state) =>
        tr.docChanged || tr.getMeta(peekKey) === true
          ? computePeek(state, index())
          : value,
    },
    props: {
      decorations: (state) => peekKey.getState(state)?.decorations ?? null,
    },
  });
}

/** Re-read the index (it changed outside the editor). */
export function refreshEntityPeek(view: EditorView): void {
  view.dispatch(view.state.tr.setMeta(peekKey, true));
}

export function entityMatchesOf(
  state: EditorState,
): readonly ProseEntityMatch[] {
  return peekKey.getState(state)?.matches ?? [];
}

export function entityMatchAt(
  state: EditorState,
  pos: number,
  side: -1 | 1,
): ProseEntityMatch | null {
  return fromMaybe(entityPeekMatchAt(entityMatchesOf(state), pos, side));
}

/** Hover and resting-caret peek card for entity names. */
export function peekHoverSource(context: ProsePeekContext): HoverSource {
  return {
    hoverDelayMs: HOVER_TIME_MS,
    caretDelayMs: CARET_PEEK_MS,
    at(view, pos, side) {
      const match = entityMatchAt(view.state, pos, side);
      if (match === null) return null;
      return {
        from: match.from,
        to: match.to,
        above: true,
        className: "zw-prose-popover-peek",
        render: () =>
          renderEntityPeekCard(match.entry, {
            contentWidth: editorContentWidth(view.dom),
            rootPath: context.rootPath(),
            manuscript: context.manuscript(),
            onOpen: context.openEntry,
          }),
      };
    },
  };
}
